import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import { nextBlockId } from '@aytm/app/stats/page/question-views/cq/components/partial/lib/block-id'
import usePartialState from '@aytm/app/stats/page/question-views/cq/components/partial/states/usePartialState'

import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import Chart from '@aytm/app/stats/page/question-views/cq/components/partial/Chart'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import ItemsList from '@aytm/app/stats/page/question-views/cq/components/partial/ItemsList'
import { isNoneChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

// The correlation-finder partial: the chart over the pair of patterns the researcher crossed, and
// the table of the same pair beneath it.
//
// The shell owns the mount, the payload it draws from and the report-param write. Everything the
// researcher sees and moves is here, and the shell answers the exporters for all of it through the
// api this block hands up.

const Block = ({ model, data, params, paramsRevision, onReady, onStateChange, onViewOptionsClick }) => {
  // Minted once per mounted block and handed to both halves, so the chart and the grid drawing one
  // pair answer to each other and to no other pair on the page.
  const blockId = useMemo(() => nextBlockId(), [])

  const {
    groupType,
    axisType,
    chartMode,
    zoomLevel,
    legendOpen,
    onChartReady,
    onChartModeChange,
    onZoomChange,
    onGroupTypeChange,
    onAxisTypeChange,
    onToggleLegend
  } = usePartialState({
    params,
    paramsRevision,
    questionKey: model.key,
    format: model.format,
    headers: model.headers,
    batches: model.batches,
    answer: model.answer,
    answerType: model.answerType,
    ignoreAmount: model.ignoreAmount,
    ignorePercents: model.ignorePercents,
    questionChartMode: model.chartMode,
    mode: model.mode,
    onReady,
    onStateChange
  })

  return (
    <BlockWrapper>
      {isComponentOn(model.components, 'chart') && !isNoneChartMode(model.chartMode, data.chartMode) && (
        <ChartPlaceholder isLoading={'loading' === model.answer}>
          <Chart
            questionId={model.id}
            questionKey={model.key}
            qType={model.type}
            chartMode={chartMode}
            questionChartMode={model.chartMode}
            globalChartMode={data.chartMode}
            onChartModeChange={onChartModeChange}
            zoomLevel={zoomLevel}
            globalZoomLevel={data.zoomLevel}
            onZoomChange={onZoomChange}
            batches={model.batches}
            groupType={groupType}
            axisType={axisType}
            answer={model.answer}
            answerType={model.answerType}
            format={model.format?.chart || {}}
            ignoreAmount={model.ignoreAmount}
            ignorePercents={model.ignorePercents}
            colorTheme={data.colorTheme}
            chartLegendMode={data.chartLegendMode}
            confidenceLevel={model.confidenceLevel || data.confidenceLevel}
            significanceLevel={model.significanceLevel || data.significanceLevel}
            confidenceLevels={data.confidenceLevels}
            features={data.features}
            percentDecimals={model.percentDecimals ?? data.percentDecimals}
            mode={model.mode}
            legendOpen={legendOpen}
            isReportXpert={data.isReportXpert}
            blockId={blockId}
            onChartReady={onChartReady}
            onGroupTypeChange={onGroupTypeChange}
            onAxisTypeChange={onAxisTypeChange}
            onLegendClose={onToggleLegend}
            onViewOptionsClick={onViewOptionsClick}
          />
        </ChartPlaceholder>
      )}
      {isComponentOn(model.components, 'table') && (
        <ItemsList model={model} data={data} blockId={blockId} onToggleLegend={onToggleLegend} />
      )}
    </BlockWrapper>
  )
}

Block.displayName = 'CorrelationFinderPartialBlock'

// What the shell hands in: the pair and what was answered for it (`model`), the report-wide
// settings every question is drawn under (`data`), and the sections of the report params this
// partial owns.
Block.propTypes = {
  model: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    key: PropTypes.string,
    type: PropTypes.string,
    text: PropTypes.array,
    batches: PropTypes.array,
    answer: answerPropType,
    answerType: PropTypes.string,
    mode: PropTypes.oneOf(['sigtest', 'crosstab']),
    headers: PropTypes.object,
    format: PropTypes.object,
    components: PropTypes.object,
    chartMode: PropTypes.string,
    ignoreAmount: PropTypes.bool,
    ignorePercents: PropTypes.bool,
    confidenceLevel: PropTypes.number,
    significanceLevel: PropTypes.number,
    percentDecimals: PropTypes.number
  }).isRequired,
  data: PropTypes.shape({
    chartMode: PropTypes.string,
    zoomLevel: PropTypes.string,
    colorTheme: PropTypes.array,
    chartLegendMode: PropTypes.string,
    confidenceLevel: PropTypes.number,
    significanceLevel: PropTypes.number,
    percentDecimals: PropTypes.number,
    confidenceLevels: PropTypes.array,
    features: PropTypes.object,
    isReportXpert: PropTypes.bool
  }).isRequired,
  paramsRevision: PropTypes.number,
  params: PropTypes.shape({
    cm: PropTypes.string,
    cf: PropTypes.object,
    z: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
  }),
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onViewOptionsClick: PropTypes.func
}

export default Block
